<script>
import { http } from '@/helpers/easyindustriaapi/config'

export default {
  name: 'ResumoRepasseMe',
  data() {
    const today = new Date()
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0)
    return {
      isLoading: false,
      isLoadingPdf: false,
      error: null,
      report: null,
      filters: {
        data_inicial: this.formatDateForInput(firstDay),
        data_final: this.formatDateForInput(lastDay)
      },
      fields: [
        { key: 'paciente', label: 'Paciente', sortable: true, tdClass: 'text-left', thClass: 'text-center' },
        { key: 'quantidade', label: 'Quantidade', sortable: true, tdClass: 'text-right', thClass: 'text-center' },
        { key: 'valor', label: 'Valor (R$)', sortable: true, tdClass: 'text-right', thClass: 'text-center' }
      ]
    }
  },
  methods: {
    formatDateForInput(date) {
      const d = new Date(date)
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },
    async generateReport() {
      this.error = null
      this.report = null
      this.isLoading = true
      try {
        const params = {
          data_inicial: this.filters.data_inicial,
          data_final: this.filters.data_final
        }
        const res = await http.get('/relatorios/resumo-repasse-profissional/me', { params })
        this.report = res.data
      } catch (e) {
        const data = e.response?.data
        const msg = data?.message || (data?.errors ? Object.values(data.errors).flat().join(' ') : null) || 'Erro ao gerar relatório.'
        this.error = msg
      } finally {
        this.isLoading = false
      }
    },
    formatMoney(value) {
      if (value == null) return '0,00'
      return Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    async openPdf() {
      this.isLoadingPdf = true
      this.error = null
      try {
        const params = {
          data_inicial: this.filters.data_inicial,
          data_final: this.filters.data_final
        }
        const res = await http.get('/relatorios/resumo-repasse-profissional/me/pdf', {
          params,
          responseType: 'blob'
        })
        const blob = new Blob([res.data], { type: 'application/pdf' })
        const url = URL.createObjectURL(blob)
        window.open(url, '_blank', 'noopener,noreferrer')
        setTimeout(() => URL.revokeObjectURL(url), 60000)
      } catch (e) {
        const data = e.response?.data
        let msg = 'Erro ao gerar PDF.'
        if (data && typeof data === 'object' && !(data instanceof Blob)) {
          if (data.message) msg = data.message
          else if (data.errors) msg = Object.values(data.errors).flat().join(' ')
        } else if (typeof data === 'string') msg = data
        this.error = msg
      } finally {
        this.isLoadingPdf = false
      }
    }
  }
}
</script>

<template>
  <div class="resumo-repasse-me">
    <h5 class="card-title mb-3">Resumo Repasse (seus atendimentos)</h5>

    <div class="row">
      <div class="col-md-4">
        <b-form-group label="Data inicial" label-for="data_inicial_me">
          <b-form-input
            id="data_inicial_me"
            v-model="filters.data_inicial"
            type="date"
          />
        </b-form-group>
      </div>
      <div class="col-md-4">
        <b-form-group label="Data final" label-for="data_final_me">
          <b-form-input
            id="data_final_me"
            v-model="filters.data_final"
            type="date"
          />
        </b-form-group>
      </div>
      <div class="col-md-4 d-flex align-items-end pb-3">
        <b-button
          variant="primary"
          :disabled="isLoading"
          class="mr-2"
          @click="generateReport"
        >
          <b-spinner v-if="isLoading" small class="mr-1" />
          {{ isLoading ? 'Gerando...' : 'Gerar Relatório' }}
        </b-button>
        <b-button
          variant="outline-primary"
          :disabled="isLoadingPdf"
          @click="openPdf"
        >
          <b-spinner v-if="isLoadingPdf" small class="mr-1" />
          {{ isLoadingPdf ? 'Abrindo...' : 'Abrir PDF' }}
        </b-button>
      </div>
    </div>

    <b-alert v-if="error" variant="danger" show dismissible @dismissed="error = null">
      {{ error }}
    </b-alert>

    <div v-if="report" class="row mt-3">
      <div class="col-12">
        <h6 class="mb-2">Período: {{ report.periodo }} — {{ report.profissional }}</h6>
        <b-table
          :items="report.itens || []"
          :fields="fields"
          striped
          responsive="sm"
          show-empty
          empty-text="Nenhum atendimento no período."
          foot-clone
        >
          <template #cell(valor)="data">
            {{ formatMoney(data.value) }}
          </template>
          <template #foot(valor)="">
            <strong>{{ formatMoney(report.total_geral) }}</strong>
          </template>
          <template #foot(quantidade)="">
            <strong>{{ (report.itens || []).reduce((s, i) => s + (i.quantidade || 0), 0) }}</strong>
          </template>
          <template #foot(paciente)="">
            <strong>Total geral</strong>
          </template>
        </b-table>
      </div>
    </div>

    <div v-else-if="!report && !isLoading && !error" class="row mt-3">
      <div class="col-12 text-center py-4 text-muted">
        Selecione o período e clique em "Gerar Relatório" para visualizar seu resumo de repasses.
      </div>
    </div>
  </div>
</template>

<style scoped>
.resumo-repasse-me .table th {
  font-weight: 600;
  vertical-align: middle;
}
.resumo-repasse-me .table td {
  vertical-align: middle;
}
</style>
